import React from "react";
import {
  Grid,
  Card,
  CardMedia,
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const PoolCards = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [pools, setPools] = React.useState([]);
  const [joining, setJoining] = React.useState(null);
  const [joined, setJoined] = React.useState(null);

  const brand = theme.palette.primary.main;
  const onBrand = theme.palette.getContrastText(brand);

  React.useEffect(() => {
    getPools();
  }, []);

  const getPools = async () => {
    const result = await request("pool/joinable");
    if (result.success) {
      setPools(result.pools || []);
    }
  };

  const handleJoin = async (pool) => {
    setJoining(pool._id);
    const result = await request("pool/join", "post", { poolId: pool._id });
    setJoining(null);
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    setPools((prev) => prev.filter((p) => p._id !== pool._id));
    setJoined({ ...result.pool, missing: result.missing });
  };

  const closeJoined = () => setJoined(null);

  // Same hand-off My Events uses when somebody cannot check in yet: Profile
  // reads this off location.state and runs the tour, highlighting the list's
  // question group so they land on the exact row they need to fill in.
  const goToProfile = () => {
    const target = joined;
    setJoined(null);
    navigate("/profile", {
      state: {
        profileTour: {
          questionGroupId: target?.questionGroupId || null,
          questionGroupName: target?.questionGroupName,
          name: !!target?.missing?.name,
          photo: !!target?.missing?.photo,
        },
      },
    });
  };

  return (
    <>
      {pools.map((pool, index) => (
        <Grid key={pool._id} item xs={12}>
          <Card
            elevation={0}
            sx={{
              width: "min(345px, calc(100vw - 40px))",
              backgroundColor: brand,
              color: onBrand,
              borderRadius: "16px",
              overflow: "hidden",
              opacity: 0,
              animation: "poolCardIn 420ms ease-out forwards",
              animationDelay: `${index * 80}ms`,
              "@keyframes poolCardIn": {
                from: { opacity: 0, transform: "translateY(8px)" },
                to: { opacity: 1, transform: "none" },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
                opacity: 1,
              },
            }}
          >
            {pool.imageUrl ? (
              <CardMedia
                sx={{ height: 168 }}
                image={pool.imageUrl}
                title={pool.name}
              />
            ) : null}

            <Box sx={{ padding: "20px 20px 22px 20px" }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  opacity: 0.75,
                  marginBottom: "10px",
                }}
              >
                By invitation
              </Typography>

              <Typography
                sx={{
                  fontSize: "1.35rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  marginBottom: pool.description ? "6px" : "18px",
                }}
              >
                {pool.name}
              </Typography>

              {pool.description ? (
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    lineHeight: 1.45,
                    opacity: 0.85,
                    marginBottom: "18px",
                  }}
                >
                  {pool.description}
                </Typography>
              ) : null}

              <Button
                fullWidth
                disableElevation
                disabled={joining === pool._id}
                onClick={() => handleJoin(pool)}
                sx={{
                  backgroundColor: onBrand,
                  color: brand,
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "99rem",
                  minHeight: "38px",
                  "&:hover": { backgroundColor: onBrand, opacity: 0.9 },
                  "&.Mui-disabled": { backgroundColor: onBrand, opacity: 0.6 },
                }}
              >
                {joining === pool._id ? "Joining…" : "Join the list"}
              </Button>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  lineHeight: 1.4,
                  opacity: 0.7,
                  marginTop: "12px",
                  textAlign: "center",
                }}
              >
                Members get invited to events that aren't open to the public.
              </Typography>
            </Box>
          </Card>
        </Grid>
      ))}

      <Dialog open={!!joined} onClose={closeJoined}>
        <DialogTitle>You're on the list</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {joined?.questionGroupName
              ? `You have been added to ${joined?.name}. To be considered for invitations, head to your profile and complete your ${joined.questionGroupName} profile.`
              : `You have been added to ${joined?.name}. To be considered for invitations, head to your profile and make sure it is complete.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeJoined}>Not now</Button>
          <Button variant="contained" onClick={goToProfile}>
            Go to profile
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PoolCards;
